import React from 'react';

import { Switch, SwitchProps } from '@mui/material';
import { FieldValues, Path, useController, UseControllerProps } from 'react-hook-form';
import { LbFormControlLabel, LbFormControlLabelProps } from '@infoods/lifebase-ui';

type LbSwitchProps<T extends FieldValues, TName extends Path<T>> = Omit<
  UseControllerProps<T, TName>,
  'defaultValue'
> &
  Pick<SwitchProps, 'disabled' | 'defaultChecked' | 'color'> &
  Pick<LbFormControlLabelProps, 'className' | 'label' | 'align' | 'font'> & {
    checkedValue?: T[TName];
    uncheckedValue?: T[TName];
  };

const LbSwitch = <T extends FieldValues, TName extends Path<T>>({
  control,
  name,
  defaultChecked = false,
  label,
  className,
  rules,
  disabled,
  color,
  align,
  font,
  shouldUnregister,
  checkedValue,
  uncheckedValue,
}: LbSwitchProps<T, TName>) => {
  const {
    field: { value, onChange, onBlur, ref, name: fieldName },
  } = useController<any>({
    control,
    name,
    defaultValue: defaultChecked,
    rules,
    shouldUnregister,
  });

  const onValueChange = (_: any, checked: boolean) => {
    if (checkedValue && uncheckedValue) {
      onChange(checked ? checkedValue : uncheckedValue);
    } else {
      onChange(checked);
    }
  };

  const checked = checkedValue && uncheckedValue ? value === checkedValue : value;

  return (
    <LbFormControlLabel
      control={
        <Switch
          checked={checked}
          onChange={onValueChange}
          slotProps={{ input: { ref } }}
          name={fieldName}
          onBlur={onBlur}
          color={color}
        />
      }
      disabled={disabled}
      label={label}
      className={className}
      align={align}
      font={font}
    />
  );
};

export { LbSwitch };
